<template>
  <div class="html5-tutorial">
    <h1 class="htmlh1">HTML5の基礎構造</h1>
    <p>HTML5の基本的な構造と、主要なタグについて学びます。</p>
    <!-- <div class="video"><div class = "video-container"><iframe  src="https://www.youtube.com/embed/S1l5LbpDWQU" frameborder="0" allowfullscreen></iframe></div></div> -->

    <section>
      <h2 class="htmlh2">1. HTML文書構造</h2>
      <p>HTML文書の基本的な構成は以下の通りです：</p>
      <pre class = "htmlpre showup">
&lt;!DOCTYPE html&gt;
&lt;html&gt;
&lt;head&gt;
  &lt;title&gt;サンプルページ&lt;/title&gt;
&lt;/head&gt;
&lt;body&gt;
  &lt;h1&gt;見出し&lt;/h1&gt;
  &lt;p&gt;これは段落です。&lt;/p&gt;
&lt;/body&gt;
&lt;/html&gt;
      </pre>
      <textarea class = "htmltextarea" v-model="codeBlocks[0].userCode" placeholder="ここにHTMLコードを入力してください"></textarea>
      <button class = "runbutton" @click="runCode(0)">実行</button>
      <iframe class="htmliframe showup" :ref="el => (codeBlocks[0].iframe = el)"></iframe>
    </section>

    <!-- <section>
      <textarea class = "htmltextarea" v-model="codeBlocks[1].userCode" placeholder="ここにHTMLコードを入力してください"></textarea>
      <button class = "runbutton" @click="runCode(1)">実行</button>
      <iframe class="htmliframe showup" :ref="el => (codeBlocks[1].iframe = el)"></iframe>
    </section> -->

    <!-- <section>
      <textarea class = "htmltextarea" v-model="codeBlocks[2].userCode" placeholder="ここにHTMLコードを入力してください"></textarea>
      <button class = "runbutton" @click="runCode(2)">実行</button>
      <iframe class="htmliframe showup" :ref="el => (codeBlocks[2].iframe = el)"></iframe>
    </section> -->

    <section>
      <h2 class="htmlh2">2. HTMLの主要な要素</h2>
      <ul class="showup">
        <li><strong>&lt;!DOCTYPE html&gt;</strong> - HTML5文書であることを宣言</li>
        <li><strong>&lt;html&gt; &nbsp; &lt;/html&gt;</strong> - HTML文書全体を囲むルート要素 - HTML文書は、&lt;html&gt; で始まり、&lt;/html&gt; で終わります。</li>
        <li><strong>&lt;head&gt; &nbsp; &lt;/head&gt;</strong> - メタデータやスタイル情報を格納</li>
        <li><strong>&lt;body&gt; &nbsp; &lt;/body&gt;</strong> - 実際に表示されるコンテンツを含む - HTML文書の目に見える部分は、&lt;body&gt; と &lt;/body&gt; の間にあります。</li>
        <li><strong>&lt;h1&gt; &nbsp; &lt;/h1&gt;～&lt;h6&gt; &nbsp; &lt;/h6&gt;</strong> - 見出しタグ</li>
        <li><strong>&lt;p&gt; &nbsp; &lt;/p&gt;</strong> - 段落タグ</li>
      </ul>
    </section>

    <section class="code-container">
      <pre>&lt;html&gt;
    <div class="nested">
        &lt;head&gt;
        <div class="nested">
            &lt;title&gt;HTMLのタイトル&lt;/title&gt;
        </div>
        &lt;/head&gt;
    </div>
    <div class="nested">
        &lt;body&gt;
        <div class="nested">
            &lt;h1&gt;記事の見出し&lt;/h1&gt;
            <div class="nested">
                &lt;p&gt;段落&lt;/p&gt;
            </div>
            <div class="nested">
                &lt;p&gt;もう一つの段落&lt;/p&gt;
            </div>
        </div>
        &lt;/body&gt;
    </div>
&lt;/html&gt;
    </pre>
    </section>

    <section>
      <h2 class="htmlh2">3. HTML5のセマンティックタグ</h2>
      <p>HTML5では、より意味を持たせたタグが導入されました：</p>
      <ul class="showup">
        <li><strong>&lt;header&gt;</strong> - ヘッダー部分</li>
        <li><strong>&lt;nav&gt;</strong> - ナビゲーション</li>
        <li><strong>&lt;section&gt;</strong> - コンテンツのセクション</li>
        <li><strong>&lt;article&gt;</strong> - 独立した記事</li>
        <li><strong>&lt;footer&gt;</strong> - フッター部分</li>
      </ul>
    </section>

    <section>
      <h2 class="html2">専門用語対照表</h2>
      <table class ="htmltable showup" id="terminologyTable">
        <tr>
          <th>English</th>
          <th>日本語</th>
          <th>中国語</th>
        </tr>
        <tr v-for="(term, index) in terms" :key="index">
          <td @click="speak(term.english,'en-US')">{{ term.english }}</td>
          <td @click="speak(term.japanese,'ja-JP')">{{ term.japanese }}</td>
          <td @click="speak(term.chinese,'zh-CN')">{{ term.chinese }}</td>
        </tr>
      </table>

    </section>

  </div>
</template>

<style>
  @import './HTMLCss.scss';
</style>

<script setup>
import { ref, onMounted, nextTick } from "vue";

import {Terminology, updateIframe, speak} from '@/components/HTML/HTML'

//术语数据
const terms =[
  new Terminology("Tag", "タグ", "标签"),
  new Terminology("Attribute", "属性", "属性"),
  new Terminology("Element", "要素", "元素"),
  new Terminology("DOCTYPE", "ドックタイプ", "文档类型"),
  new Terminology("Head", "ヘッド", "头部"),
  new Terminology("Body", "ボディ", "主体"),
  new Terminology("Title", "タイトル", "标题"),
  new Terminology("Paragraph", "段落", "段落"),
  new Terminology("Hyperlink", "ハイパーリンク", "超链接"),
  new Terminology("List", "リスト", "列表"),
  new Terminology("Table", "テーブル", "表格"),
  new Terminology("Form", "フォーム", "表单"),
  new Terminology("Button", "ボタン", "按钮"),
  new Terminology("Semantic Tag", "セマンティックタグ", "语义化标签"),
  new Terminology("Header", "ヘッダー", "头部"),
  new Terminology("Footer", "フッター", "页脚"),
  new Terminology("Section", "セクション", "区块"),
  new Terminology("Article", "記事", "文章"),
  new Terminology("Navigation", "ナビゲーション", "导航")
];

// 使用数组存储多个 textarea、iframe
const codeBlocks = ref([
  { userCode: "", iframe: ref(null) },
  { userCode: "", iframe: ref(null) },
  { userCode: "", iframe: ref(null) }
]);

// 运行代码，只更新对应的 iframe
const runCode = (index) => {
  const block = codeBlocks.value[index];
  console.log("index:", index, "block:", block);
  console.log("block.iframe:", block.iframe);

  if (!block.iframe) {
    setTimeout(() => runCode(index), 50); // 50ms 后再检查 iframe 是否绑定
    return;
  }

  updateIframe(block.userCode, block.iframe);
};

// 在 mounted 时自动执行代码
// 在 mounted 时自动执行代码
// 在 mounted 时自动执行代码
onMounted(async () => {
  await nextTick(); // 等待 DOM 更新
  codeBlocks.value.forEach((_, index) => runCode(index));
  speak;
});

</script>